import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { BillingApiError, billingErrorMessage } from "./billingApi.js";
import { publishOrderDataChange } from "../utils/orderDataSync.js";
import { readConsolidationSaveStream, unconfirmedConsolidationSave } from "./consolidationSaveStream.js";

// Repli quand le serveur refuse sans raison : selon l'action, jamais « Impossible de charger » pour une écriture.
const reading = (method, path) => method === "GET" || /\/preview(?:\?|$)/.test(path);
export function refusalFallback(method, path) {
  if (reading(method, path)) return "Impossible de charger la consolidation.";
  const refused = /\/validate(?:\?|$)/.test(path) ? "Validation refusée" : /\/reopen(?:\?|$)/.test(path) ? "Réouverture refusée" : "Enregistrement refusé";
  return `${refused} par le serveur, sans raison donnée. Rechargez la journée, puis réessayez.`;
}

export async function consolidationRequest(path = "", { method = "GET", body, signal, onProgress } = {}) {
  const trackedSave = typeof onProgress === "function";
  const action = /\/validate$/.test(path) ? "validate" : "save";
  const stream = trackedSave && method === "POST" && ["/sheets/from-dispatch", "/preloads/from-dispatch"].includes(path);
  const response = await fetchWithSupabaseAuth(`/api/billing/consolidation${path}`, {
    method, signal, cache: "no-store",
    headers: { ...(body === undefined ? {} : { "Content-Type": "application/json" }), ...(stream ? { Accept: "text/event-stream" } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).catch(error => {
    if (trackedSave) throw unconfirmedConsolidationSave(error, action);
    if (error?.name === "AbortError") throw error;
    // Coupure de connexion : jamais le message anglais du navigateur (« Failed to fetch »).
    throw new Error(reading(method, path) ? "Connexion au serveur impossible. Vérifiez la connexion, puis cliquez sur « Recharger »."
      : "La connexion au serveur a été coupée : l’action a peut-être abouti. Rechargez la journée avant de réessayer.", { cause: error });
  });
  const payload = response.ok && stream && response.headers.get("content-type")?.includes("text/event-stream")
    ? await readConsolidationSaveStream(response, onProgress)
    : await response.json().catch(() => null);
  if (!response.ok) throw new BillingApiError(billingErrorMessage(payload, refusalFallback(method, path)), response.status, payload);
  if (trackedSave && !payload?.id) throw unconfirmedConsolidationSave(undefined, action);
  if (method !== "GET" && !/\/preview(?:\?|$)/.test(path)) publishOrderDataChange("consolidation");
  return payload;
}

export const versionPayload = (row) => ({ version: row.version, shared_versions: row.shared_versions, day_version: row.day_version,
  ...(row.pm_linked_versions ? { pm_linked_versions: row.pm_linked_versions } : {}) });

/** $/h, $/t et $ fixe d'une commande changés depuis le brouillon : le serveur rouvre et revalide ses relevés. */
export const changeInvoiceOrderRates = (invoiceId, orderId, { version, ...rates }) =>
  consolidationRequest(`/invoices/${invoiceId}/orders/${orderId}/rates`, { method: "POST", body: { version, ...rates } });

/** Jours couverts par un brouillon de relevé : le serveur reprend les prestations de la nouvelle période. */
export const changeInvoicePeriod = (invoiceId, { version, from, to }) =>
  consolidationRequest(`/invoices/${invoiceId}/period`, { method: "POST", body: { version, from_date: from, to_date: to } });
